// App.jsx (or App.tsx with minor typing)
import React, {useState, useEffect, useCallback} from "react";
import {BrowserRouter as Router, Route, Routes} from "react-router-dom";
import Header from "./components/Header";
import Footer from "./components/Footer";
import ArticlePage from "./components/ArticlePage";
import Notification from "./components/Notification";
import VerticalCarousel from "./components/VerticalCarousel";
import HomePage from "./pages/HomePage";
import ProfilePage from "./components/ProfilePage";
import SearchPage from "./components/SearchPage";
import OnboardingModal from "./components/OnboardingModal";
import {
  flattenNeuronRecommendationItems,
  getNeuronRecommendations,
  NON_COMMENT_SCOPE,
} from "./utils/getNeuronRecommendations";
import {RECOMMENDATION_CONTEXTS} from "./config/recommendationContexts";
import AllStoriesPage from "./pages/AllStoriesPage";
import ViewInterceptorProvider from "./components/ViewInterceptorProvider";

function App() {
  const [userEmail, setUserEmail] = useState(
    () => localStorage.getItem("userEmail") || ""
  );
  const [hasCompletedOnboarding, setHasCompletedOnboarding] = useState(
    () => localStorage.getItem("onboardingComplete") === "true"
  );
  const [notification, setNotification] = useState(null);
  const [debugEnabled, setDebugEnabled] = useState(
    () => localStorage.getItem("neuron:viewInterceptorDebug") === "true"
  );
  const [personalisedData, setPersonalisedData] = useState({});
  const [loadingStates, setLoadingStates] = useState({});

  // ✅ Listen for custom dispatched event
  useEffect(() => {
    const handler = (event) => {
      if (!debugEnabled) return;

      if (event.detail?.message) {
        setNotification({
          message: event.detail.message,
          status: event.detail.status || "success",
        });
      }
    };

    window.addEventListener("neuronEventLogged", handler);
    return () => window.removeEventListener("neuronEventLogged", handler);
  }, [debugEnabled]);

  useEffect(() => {
    const handler = (event) => {
      if (typeof event.detail?.enabled === "boolean") {
        setDebugEnabled(event.detail.enabled);
      }
    };

    window.addEventListener("viewInterceptorToggle", handler);
    return () => window.removeEventListener("viewInterceptorToggle", handler);
  }, []);

  useEffect(() => {
    if (!debugEnabled) {
      setNotification(null);
    }
  }, [debugEnabled]);

  const fetchRecommendationsForContext = useCallback(
    async (contextId = RECOMMENDATION_CONTEXTS.homepageForYou) => {
      const resolvedContextId =
        contextId || RECOMMENDATION_CONTEXTS.homepageForYou;
      const id = String(resolvedContextId);

      if (!userEmail) {
        console.warn(
          "User email not available. Skipping recommendation fetch."
        );
        return;
      }

      setLoadingStates((prev) => ({...prev, [id]: true}));

      try {
        const data = await getNeuronRecommendations({
          userId: userEmail,
          scope: NON_COMMENT_SCOPE,
          contextId: resolvedContextId,
        });

        setPersonalisedData((prev) => ({
          ...prev,
          [id]: flattenNeuronRecommendationItems(data)
            .filter((item) => item?.metadata?.type !== "comment"),
        }));
      } catch (err) {
        console.error(`💥 Error fetching for context "${id}":`, err);
      } finally {
        setLoadingStates((prev) => ({...prev, [id]: false}));
      }
    },
    [userEmail]
  );

  return (
    <Router>
      <div className="relative min-h-screen bg-paper text-ink">
        <Header />

        {/* ✅ Wrap the entire app content so ALL routes get the overlay + refs */}
        <ViewInterceptorProvider userId={userEmail}>
          {(viewInterceptor) => (
            <main>
              <Routes>
                <Route
                  path="/"
                  element={
                    <HomePage
                      userEmail={userEmail}
                      hasCompletedOnboarding={hasCompletedOnboarding}
                      devModeEnabled={debugEnabled}
                      personalisedData={personalisedData}
                      loadingStates={loadingStates}
                      fetchRecommendationsForContext={
                        fetchRecommendationsForContext
                      }
                      viewInterceptor={viewInterceptor}
                    />
                  }
                />
                <Route
                  path="/stories"
                  element={
                    <AllStoriesPage
                      userEmail={userEmail}
                      viewInterceptor={viewInterceptor}
                    />
                  }
                />
                <Route
                  path="/explore"
                  element={
                    <VerticalCarousel
                      userEmail={userEmail}
                      personalisedData={personalisedData}
                      loadingStates={loadingStates}
                      fetchRecommendationsForContext={
                        fetchRecommendationsForContext
                      }
                      viewInterceptor={viewInterceptor}
                    />
                  }
                />
                <Route
                  path="/article/:id"
                  element={<ArticlePage userEmail={userEmail} />}
                />
                <Route path="/search" element={<SearchPage />} />
                <Route
                  path="/profile"
                  element={
                    <ProfilePage
                      userEmail={userEmail}
                      personalisedContent={
                        personalisedData[RECOMMENDATION_CONTEXTS.homepageForYou]
                      }
                    />
                  }
                />
              </Routes>
            </main>
          )}
        </ViewInterceptorProvider>

        <Footer />

        {debugEnabled && (
          <Notification
            message={notification?.message}
            status={notification?.status}
            onClose={() => setNotification(null)}
          />
        )}

        {!hasCompletedOnboarding && (
          <OnboardingModal
            onComplete={(email) => {
              localStorage.setItem("onboardingComplete", "true");
              localStorage.setItem("userEmail", email);

              setUserEmail(email);
              setHasCompletedOnboarding(true);
            }}
          />
        )}
      </div>
    </Router>
  );
}

export default App;
